// V2.1 Consolidated Purchase QC Job — engineer's primary QC workspace.
//
// One Purchase = one page. The engineer sees every laptop in the purchase,
// picks the next PENDING one, enters the real Manufacturer Serial Number,
// then hands off to the existing per-passport QC form for the checklist.
// The per-passport form was previously reached via /admin/qc/jobs/:passport
// — kept unchanged so BC is preserved and no logic is duplicated.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ClipboardCheck, CheckCircle2, XCircle, Loader2, ScanLine, Play } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { displaySerial, isPlaceholderSerial } from "@/lib/serials";
import { useAuth, canFinishQC } from "@/lib/auth";

const STATUS_STYLE = {
  PENDING:      "bg-slate-100 text-slate-700",
  IN_PROGRESS:  "bg-sky-100 text-sky-800",
  PASSED:       "bg-emerald-100 text-emerald-800",
  FAILED:       "bg-rose-100 text-rose-800",
};

export default function PurchaseQCJobPage() {
  const { purchase_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [serialInput, setSerialInput] = useState({}); // per-item scratchpad
  const [savingItemId, setSavingItemId] = useState(null);

  const canOperate = canFinishQC(user);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data: d } = await api.get(`/qc/jobs/purchase/${purchase_id}`);
      setData(d);
    } catch (e) { setErr(errorToMessage(e, "Failed to load QC Job")); }
    finally { setLoading(false); }
  }, [purchase_id]);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const job = data?.job;

  const nextItem = useMemo(
    () => items.find((it) => it.status === "PENDING" || it.status === "IN_PROGRESS"),
    [items],
  );

  const captureSerial = async (item) => {
    const sn = (serialInput[item.id] || "").trim();
    if (!sn) { setErr("Enter or scan a Manufacturer Serial Number first"); return; }
    setSavingItemId(item.id); setErr(null);
    try {
      await api.post(
        `/qc/jobs/purchase/${purchase_id}/items/${item.id}/serial`,
        { manufacturer_serial: sn },
      );
      // Navigate to the existing per-passport QC form for the checklist —
      // reused, not rebuilt.
      navigate(`/admin/qc/jobs/${item.passport_id}`);
    } catch (e) {
      setErr(errorToMessage(e, "Could not capture serial number"));
    } finally {
      setSavingItemId(null);
    }
  };

  const openChecklistForCompleted = (item) => {
    // Read-only view of a completed item's QC record — reuses existing page.
    navigate(`/admin/qc/jobs/${item.passport_id}`);
  };

  if (loading) return <div className="text-slate-400 text-sm">Loading QC Job…</div>;
  if (!job) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate(`/admin/purchases/${purchase_id}`)}
          className="text-sm text-slate-600 inline-flex items-center gap-1">
          <ArrowLeft size={14}/> Back to Purchase
        </button>
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          No QC Job exists for this Purchase yet. Submit and receive the Purchase first.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="purchase-qc-job-page">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(`/admin/purchases/${purchase_id}`)}
          className="text-sm text-slate-600 inline-flex items-center gap-1">
          <ArrowLeft size={14}/> Back to Purchase
        </button>
        <div className="text-xs text-slate-500">
          One consolidated QC Job per Purchase · engineers process one laptop at a time
        </div>
      </div>

      {/* Header + progress */}
      <section className="bg-white border border-slate-200 rounded-md p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">Purchase QC Job</div>
            <div className="text-lg font-semibold text-slate-900">
              {job.purchase_no}
              {job.vendor_name && <span className="ml-2 text-slate-500 font-normal">· {job.vendor_name}</span>}
            </div>
          </div>
          <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
            job.status === "COMPLETED" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"
          }`}>
            {job.status === "COMPLETED" ? <CheckCircle2 size={14}/> : <ClipboardCheck size={14}/>}
            {job.status}
          </span>
        </div>
        <div className="grid grid-cols-4 gap-3 text-sm">
          <ProgressTile label="Total Units" value={job.total_units}/>
          <ProgressTile label="Passed" value={job.passed_count} tone="emerald"/>
          <ProgressTile label="Failed" value={job.failed_count} tone="rose"/>
          <ProgressTile label="Pending" value={job.pending_count} tone="sky"/>
        </div>
        <div className="mt-3 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-emerald-500 float-left" style={{ width: `${(100 * job.passed_count / Math.max(1, job.total_units))}%` }}/>
          <div className="h-full bg-rose-500 float-left" style={{ width: `${(100 * job.failed_count / Math.max(1, job.total_units))}%` }}/>
        </div>
      </section>

      {err && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{err}</div>
      )}

      {/* Next laptop callout */}
      {nextItem && canOperate && (
        <section className="bg-white border-2 border-blue-200 rounded-md p-4" data-testid="next-laptop">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-blue-600">Next Laptop</div>
              <div className="text-base font-semibold text-slate-900">
                #{nextItem.sequence_no} · {nextItem.passport_uid || nextItem.passport_no}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {[nextItem.brand, nextItem.model, nextItem.processor].filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${STATUS_STYLE[nextItem.status]}`}>
              {nextItem.status}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <ScanLine size={16} className="text-slate-400"/>
            <input
              autoFocus
              type="text"
              placeholder={
                isPlaceholderSerial(nextItem.manufacturer_serial)
                  ? "Scan or type real Manufacturer Serial Number"
                  : `Serial already captured (${nextItem.manufacturer_serial}) — retype to change`
              }
              value={serialInput[nextItem.id] ?? (isPlaceholderSerial(nextItem.manufacturer_serial) ? "" : nextItem.manufacturer_serial)}
              onChange={(e) => setSerialInput((prev) => ({ ...prev, [nextItem.id]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") captureSerial(nextItem); }}
              className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-blue-600"
              data-testid="input-next-serial"
            />
            <button
              onClick={() => captureSerial(nextItem)}
              disabled={savingItemId === nextItem.id}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-60"
              data-testid="btn-start-qc"
            >
              {savingItemId === nextItem.id ? <Loader2 className="animate-spin" size={14}/> : <Play size={14}/>}
              Start QC
            </button>
          </div>
        </section>
      )}

      {/* Full item list */}
      <section className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left w-12">#</th>
              <th className="px-3 py-2 text-left">Passport ID</th>
              <th className="px-3 py-2 text-left">Manufacturer Serial</th>
              <th className="px-3 py-2 text-left">Configuration</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100" data-testid={`qc-item-${it.sequence_no}`}>
                <td className="px-3 py-2 font-mono text-slate-500">{it.sequence_no}</td>
                <td className="px-3 py-2 font-mono text-slate-900" data-testid={`qc-item-passport-${it.passport_id}`}>
                  {it.passport_uid || it.passport_no}
                  {it.item_type && it.item_type !== "laptop" && (
                    <span className="ml-1 inline-flex items-center rounded-full bg-indigo-100 border border-indigo-200 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider text-indigo-800">
                      {it.item_type}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-slate-700">
                  {displaySerial(it.manufacturer_serial)}
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {[it.brand, it.model, it.processor].filter(Boolean).join(" · ") || "—"}
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[it.status]}`}>
                    {it.status === "PASSED" && <CheckCircle2 size={12}/>}
                    {it.status === "FAILED" && <XCircle size={12}/>}
                    {it.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {(it.status === "PENDING" || it.status === "IN_PROGRESS") && canOperate ? (
                    <button
                      onClick={() => setSerialInput((p) => ({ ...p, [it.id]: p[it.id] ?? "" }))}
                      className="text-xs text-blue-700 hover:underline"
                      data-testid={`btn-focus-item-${it.sequence_no}`}
                    >
                      {it === nextItem ? "↑ Above" : "Skip to"}
                    </button>
                  ) : (
                    <button
                      onClick={() => openChecklistForCompleted(it)}
                      className="text-xs text-slate-500 hover:underline"
                    >
                      View
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function ProgressTile({ label, value, tone = "slate" }) {
  const toneMap = {
    slate:   "text-slate-900",
    emerald: "text-emerald-700",
    rose:    "text-rose-700",
    sky:     "text-sky-700",
  };
  return (
    <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className={`text-xl font-semibold tabular-nums ${toneMap[tone] || toneMap.slate}`}>{value}</div>
    </div>
  );
}
